"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Key, MessageCircle } from "lucide-react";
export default function TheProcess() {
  const phase1 = useRevealOnScroll({
    threshold: 0.2
  });
  const phase2 = useRevealOnScroll({
    threshold: 0.2
  });
  const phase3 = useRevealOnScroll({
    threshold: 0.2
  });
  const phase4 = useRevealOnScroll({
    threshold: 0.2
  });
  const phase5 = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="the-process" className="section bg-background py-16 px-6 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <h2 className="font-display font-bold text-center mb-4 !font-sans !text-[hsl(46,65%,52%)] !text-[40px]">My Five Step Family Wisdom Legacy Architecture Process</h2>
        <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto !text-xl !font-medium">
          A six-week immersive experience designed to distill your life's wisdom into a permanent legacy sanctuary.
        </p>

        <div className="mb-20 max-w-4xl mx-auto space-y-[30px] px-5 sm:px-0">
          <div ref={phase1.ref} className={`border-[1.5px] border-[#D4AF37] rounded-[4px] bg-[hsl(65,30%,96%)] p-[25px] transition-all duration-[600ms] ease-out ${phase1.isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`} style={{
          transitionDelay: "0ms"
        }}>
            <div className="text-center mb-6">
              <span className="font-[var(--font-cinzel)] font-thin text-[80px] leading-none text-[#D4AF37] block">
                1
              </span>
            </div>
            <h3 className="font-display font-semibold text-2xl text-[#001F3F] mb-4 text-center">
              The Foundation (Week 1)
            </h3>
            <p className="font-[var(--font-libre-baskerville)] text-lg leading-relaxed text-[#001F3F] text-left">Following our initial consultation, we begin with a 30-minute Vision Session. Here, I align on your selected Life Pillars and provide you with my bespoke "Heritage Preparation Guide." This phase is designed to awaken your memories, reflect on the strategic prompt questions and prepare the "Memory Box" of photos that will illustrate your story.
</p>
          </div>

          <div ref={phase2.ref} className={`border-[1.5px] border-[#D4AF37] rounded-[4px] bg-[hsl(65,30%,96%)] p-[25px] transition-all duration-[600ms] ease-out ${phase2.isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`} style={{
          transitionDelay: "150ms"
        }}>
            <div className="text-center mb-6">
              <span className="font-[var(--font-cinzel)] font-thin text-[80px] leading-none text-[#D4AF37] block">
                2
              </span>
            </div>
            <h3 className="font-display font-semibold text-2xl text-[#001F3F] mb-4 text-center">
              The Blueprint (Week 2)
            </h3>
            <p className="font-[var(--font-libre-baskerville)] text-lg leading-relaxed text-[#001F3F] text-left">
              We begin with a series of intentional, deep-dive conversations. Using techniques honed over three decades, I guide you through a "wisdom audit" - uncovering the stories, values, and pivotal moments that define your journey. My goal is to capture your authentic voice, not just the facts.
            </p>
          </div>

          <div ref={phase3.ref} className={`border-[1.5px] border-[#D4AF37] rounded-[4px] bg-[hsl(65,30%,96%)] p-[25px] transition-all duration-[600ms] ease-out ${phase3.isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`} style={{
          transitionDelay: "300ms"
        }}>
            <div className="text-center mb-6">
              <span className="font-[var(--font-cinzel)] font-thin text-[80px] leading-none text-[#D4AF37] block">
                3
              </span>
            </div>
            <h3 className="font-display font-semibold text-2xl text-[#001F3F] mb-4 text-center">
              The Synthesis & Multimedia Curation (Weeks 3–4)
            </h3>
            <p className="font-[var(--font-libre-baskerville)] text-lg leading-relaxed text-[#001F3F] text-left">
              This is where my 30 years of experience comes to the fore. I take the raw data - hours of audio, stacks of photos and scattered notes, and identify the "Golden Threads". I synthesize and concise these elements into a powerful narrative, ensuring that your most important life lessons are clear, impactful and easy for future generations to digest. I then carefully curate your high-resolution images & text and pair them with "Golden Wisdom" audio clips - short, poignant recordings of your voice. This creates a multi-sensory experience that allows your future generations to hear your laughter and see your world exactly as it was.
            </p>
          </div>

          <div ref={phase4.ref} className={`border-[1.5px] border-[#D4AF37] rounded-[4px] bg-[hsl(65,30%,96%)] p-[25px] transition-all duration-[600ms] ease-out ${phase4.isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`} style={{
          transitionDelay: "450ms"
        }}>
            <div className="text-center mb-6">
              <span className="font-[var(--font-cinzel)] font-thin text-[80px] leading-none text-[#D4AF37] block">
                4
              </span>
            </div>
            <h3 className="font-display font-semibold text-2xl text-[#001F3F] mb-4 text-center">
              The Refinement (Week 5)
            </h3>
            <p className="font-[var(--font-libre-baskerville)] text-lg leading-relaxed text-[#001F3F] text-left">
              A private review and technical polish to ensure your Heritage Compendium and Heritage Experience is accurate and absolute. This is a high-end digital experience that combines the elegance of a digital book with the interactivity of modern technology.
            </p>
          </div>

          <div ref={phase5.ref} className={`border-[1.5px] border-[#D4AF37] rounded-[4px] bg-[hsl(65,30%,96%)] p-[25px] transition-all duration-[600ms] ease-out ${phase5.isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`} style={{
          transitionDelay: "600ms"
        }}>
            <div className="text-center mb-6">
              <span className="font-[var(--font-cinzel)] font-thin text-[80px] leading-none text-[#D4AF37] block">
                5
              </span>
            </div>
            <h3 className="font-display font-semibold text-2xl text-[#001F3F] mb-4 text-center">
              The Final Masterpiece (Week 6)
            </h3>
            <p className="font-[var(--font-libre-baskerville)] text-lg leading-relaxed text-[#001F3F] text-left">
              Your family legacy digital archives are finalised. You receive your signature Heritage Compendium, activation of your interactive Heritage Experience, and secure storage of all master files within your private family folder for one year.
            </p>
          </div>
        </div>

        <div className="flex flex-col items-center">
          <Button asChild className="cta-light w-full md:w-auto max-w-lg whitespace-normal break-words px-6 py-4 min-h-[60px] text-sm sm:text-base font-semibold rounded-lg shadow-medium hover:shadow-strong hover:scale-[1.02] active:scale-100 transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] flex items-center justify-center text-center">
            <Link to="https://calendly.com/ancelfernandes/30-min-strategy-call" newTab>
              <span className="block text-center">BOOK COMPLIMENTARY CONSULTATION CALL</span>
            </Link>
          </Button>
          <p className="font-[var(--font-libre-baskerville)] italic text-[14px] text-[#D4AF37] text-center mt-[10px] mb-[15px] flex items-center justify-center gap-2">
            <Key className="size-4" />
            <span>Includes a private viewing of <strong><em>The Heritage Experience</em></strong> and <strong><em>The Heritage Compendium</em></strong>.</span>
          </p>
          
          <div className="flex justify-center w-full" style={{
          paddingTop: '15px'
        }}>
            <Button asChild variant="ghost" className="w-full md:w-auto max-w-lg whitespace-normal break-words px-6 py-3 text-sm font-semibold rounded-lg border-[1.5px] border-[#D4AF37] text-[#D4AF37] bg-transparent hover:bg-[#D4AF37] hover:text-white transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] flex items-center justify-center gap-2">
              <Link to="https://wa.me/971504585183?text=I%20am%20interested%20in%20learning%20more%20about%20how%20I%20could%20preserve%20my%20family%20wisdom%20legacy" newTab>
                <MessageCircle className="size-5" />
                <span>ENQUIRE VIA WHATSAPP</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}